import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CreateIssueBody, IssueType, Priority } from '@tasks/contracts';

import { listComponents } from '../../api/components';
import { createIssue } from '../../api/issues';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Select, type SelectOption } from '../../components/Select';
import { Textarea } from '../../components/Textarea';
import { useToast } from '../../components/Toast';
import { useProjectMembers } from '../../hooks/useProject';
import { useUiStore } from '../../stores/uiStore';
import { humanizeError } from '../../lib/errors';
import { useFormState } from '../../lib/useFormState';
import { ISSUE_TYPE_ORDER, PRIORITY_ORDER, issueTypeLabels, priorityLabels } from '../../i18n/enums';
import styles from './QuickCreateIssueModal.module.css';

const TYPE_OPTIONS: readonly SelectOption<IssueType>[] = ISSUE_TYPE_ORDER.map((value) => ({
  value,
  label: issueTypeLabels[value],
}));

const PRIORITY_OPTIONS: readonly SelectOption<Priority>[] = PRIORITY_ORDER.map((value) => ({
  value,
  label: priorityLabels[value],
}));

type FormValues = {
  readonly type: IssueType;
  readonly summary: string;
  readonly description: string;
  readonly priority: Priority;
  readonly assigneeId: string | null;
  readonly storyPoints: string;
  readonly componentId: string | null;
};

const INITIAL: FormValues = {
  type: 'task',
  summary: '',
  description: '',
  priority: 'medium',
  assigneeId: null,
  storyPoints: '',
  componentId: null,
};

function validate(values: FormValues): Partial<Record<keyof FormValues, string>> {
  const errors: Partial<Record<keyof FormValues, string>> = {};
  if (values.summary.trim().length === 0) {
    errors.summary = 'Введите заголовок';
  }
  if (values.summary.length > 255) {
    errors.summary = 'Не больше 255 символов';
  }
  const points = values.storyPoints.trim();
  if (points.length > 0) {
    const parsed = Number(points);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      errors.storyPoints = 'От 0 до 100';
    }
  }
  return errors;
}

/** UC-ISS-01: открывается по «C» или «Создать», фокус на заголовке. */
export function QuickCreateIssueModal() {
  const { key } = useParams<{ key: string }>();
  const isOpen = useUiStore((state) => state.isQuickCreateOpen);
  const setOpen = useUiStore((state) => state.setQuickCreateOpen);
  const members = useProjectMembers(key);
  const queryClient = useQueryClient();
  const toast = useToast();
  const form = useFormState<FormValues>(INITIAL, validate);

  // Справочник компонентов нужен только когда он непустой: пустое поле в
  // быстром создании было бы шумом (docs/13 §7).
  const componentsQuery = useQuery({
    queryKey: queryKeys.projects.components(key ?? ''),
    queryFn: ({ signal }) => listComponents(key as string, { signal }),
    enabled: Boolean(key) && isOpen,
  });
  const components = componentsQuery.data ?? [];

  // Сброс при закрытии, а не при открытии: иначе Safari успевает
  // сфокусировать «Заголовок» со старым значением от прошлого открытия.
  useEffect(() => {
    if (!isOpen) {
      form.reset({ ...INITIAL });
    }
  }, [isOpen]);

  const mutation = useMutation({
    mutationFn: (body: CreateIssueBody) => createIssue(key as string, body),
    onSuccess: (issue) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backlog.view(key ?? '') });
      void queryClient.invalidateQueries({ queryKey: queryKeys.board.root(key ?? '') });
      toast.success(`${issue.key} создана`);
      setOpen(false);
      form.reset();
    },
    onError: (cause: unknown) => {
      // UC-ISS-01 AC3: при ошибке введённые данные остаются в форме.
      toast.error(humanizeError(cause).message, { code: humanizeError(cause).code });
    },
  });

  const handleSubmit = (): void => {
    if (!key || !form.validate()) {
      return;
    }
    const points = form.values.storyPoints.trim();
    mutation.mutate({
      type: form.values.type,
      summary: form.values.summary.trim(),
      description: form.values.description,
      priority: form.values.priority,
      assigneeId: form.values.assigneeId,
      parentId: null,
      epicId: null,
      storyPoints: points.length === 0 ? null : Number(points),
      dueDate: null,
      sprintId: null,
      labels: [],
      components: form.values.componentId === null ? [] : [form.values.componentId],
    });
  };

  /**
   * Выбор компонента подставляет исполнителя по умолчанию, но только если
   * пользователь ещё не выбрал его сам (docs/13 §7).
   */
  const selectComponent = (componentId: string): void => {
    form.setValue('componentId', componentId);
    const component = components.find((item) => item.id === componentId);
    const fallback = component?.defaultAssignee?.id ?? null;
    if (fallback !== null && form.values.assigneeId === null) {
      form.setValue('assigneeId', fallback);
    }
  };

  const componentOptions: readonly SelectOption<string>[] = components.map((component) => ({
    value: component.id,
    label: component.name,
  }));

  const memberOptions: readonly SelectOption<string>[] = members.map((member) => ({
    value: member.user.id,
    label: member.user.displayName,
  }));

  return (
    <Modal
      open={isOpen}
      onClose={() => setOpen(false)}
      title="Создать задачу"
      footer={
        <>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={mutation.isPending}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={mutation.isPending}>
            Создать
          </Button>
        </>
      }
    >
      <div className={styles['form']}>
        <div className={styles['row']}>
          <Select
            label="Тип"
            options={TYPE_OPTIONS}
            value={form.values.type}
            onChange={(value) => form.setValue('type', value)}
          />
          <Select
            label="Приоритет"
            options={PRIORITY_OPTIONS}
            value={form.values.priority}
            onChange={(value) => form.setValue('priority', value)}
          />
        </div>
        <Input
          label="Заголовок"
          name="summary"
          autoComplete="off"
          value={form.values.summary}
          error={form.errors.summary}
          onChange={(event) => form.setValue('summary', event.target.value)}
          onBlur={() => form.handleBlur('summary')}
        />
        <Textarea
          label="Описание"
          name="description"
          autoComplete="off"
          rows={4}
          placeholder="Поддерживается markdown"
          value={form.values.description}
          onChange={(event) => form.setValue('description', event.target.value)}
        />
        <div className={styles['row']}>
          <Select
            label="Исполнитель"
            options={memberOptions}
            value={form.values.assigneeId}
            clearable
            onClear={() => form.setValue('assigneeId', null)}
            placeholder="Не назначен"
            onChange={(value) => form.setValue('assigneeId', value)}
          />
          {componentOptions.length > 0 ? (
            <Select
              label="Компонент"
              options={componentOptions}
              value={form.values.componentId}
              clearable
              onClear={() => form.setValue('componentId', null)}
              placeholder="Без компонента"
              onChange={selectComponent}
            />
          ) : null}
        </div>
        <div className={styles['row']}>
          <Input
            label="Story points"
            name="storyPoints"
            autoComplete="off"
            inputMode="decimal"
            value={form.values.storyPoints}
            error={form.errors.storyPoints}
            onChange={(event) => form.setValue('storyPoints', event.target.value)}
            onBlur={() => form.handleBlur('storyPoints')}
          />
        </div>
      </div>
    </Modal>
  );
}
